import { ListingCardV2 } from "@/components/listings/card/ListingCardV2";
import { CompactMobileListingCard } from "@/components/listings/card/CompactMobileListingCard";
import { CarCardList } from "@/components/listings/card/CarCardList";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import type { ListingV2Response } from "@/types/listings-v2";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WarningCircle as AlertCircle } from "@phosphor-icons/react";
import { useIsMobile } from "@/hooks/use-mobile";

type ViewMode = 'grid' | 'list';

interface ListingsGridV2Props {
  listings: ListingV2Response[];
  isLoading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  onCompareToggle?: (id: string) => void;
  comparedIds?: string[];
  priorityCount?: number; // Number of images to load with priority (eager)
  viewMode?: ViewMode;
}

export const ListingsGridV2 = ({
  listings,
  isLoading = false,
  error = null,
  emptyMessage,
  onCompareToggle,
  comparedIds = [],
  priorityCount = 6, // Load first 6 images with priority by default
  viewMode = 'grid',
}: ListingsGridV2Props) => {
  const isMobile = useIsMobile();

  if (isLoading) {
    return (
      <div className={viewMode === 'list' ? "space-y-4" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"}>
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="overflow-hidden">
            <Skeleton className={viewMode === 'list' ? "h-32 w-full" : "aspect-[4/3] w-full"} />
            <CardContent className="p-4 space-y-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-8 w-1/2" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Eroare la încărcarea anunțurilor: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  if (!listings || listings.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center py-12 px-6 max-w-md">
          <div className="mb-6">
            <div className="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <AlertCircle className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">
              {emptyMessage || "Nu există anunțuri disponibile"}
            </h3>
            <p className="text-muted-foreground text-sm">
              Încearcă să ajustezi filtrele sau verifică mai târziu pentru anunțuri noi.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Mobile: compact horizontal cards; desktop layouts remain unchanged.
  if (isMobile) {
    return (
      <div className="space-y-2 listings-grid">
        {listings.map((listing) => (
          <div key={listing.id} data-flip-id={listing.id} className="listing-card-container">
            <CompactMobileListingCard listing={listing} onCompareToggle={onCompareToggle} isInComparison={comparedIds.includes(listing.id)} />
          </div>
        ))}
      </div>
    );
  }

  // Desktop: List View
  if (viewMode === 'list') {
    return (
      <div className="space-y-4 listings-grid">
        {listings.map((listing) => {
          const title = `${listing.make} ${listing.model}`;
          const slug = `${listing.make?.toLowerCase().replace(/\s+/g, '-')}-${listing.model?.toLowerCase().replace(/\s+/g, '-')}-${listing.year}-${listing.id.slice(0, 8)}`;
          
          return (
            <div key={listing.id} data-flip-id={listing.id} className="listing-card-container">
              <CarCardList
                id={listing.id}
                title={title}
                price={listing.price}
                currency={listing.currency}
                year={listing.year}
                mileageKm={listing.mileage_value || 0}
                location={listing.location_city || listing.location_country || ''}
                location_city={listing.location_city}
                location_region={listing.location_region}
                location_country={listing.location_country}
                photo={listing.photos?.[0]}
                slug={slug}
                make={listing.make}
                model={listing.model}
                promoted={listing.promoted}
                auto_promoted={listing.auto_promoted}
                is_new_listing={listing.is_new_listing}
                status={listing.status}
                vehicle_type={listing.category_code}
                verified_dealer={listing.dealer_verified ?? false}
                vin_available={listing.attributes?.vin ? true : false}
                certified_vehicle={false}
                fuel_type={listing.attributes?.fuel}
                transmission={listing.attributes?.transmission}
                mileage={listing.mileage_value || 0}
                images={listing.photos || []}
                created_at={listing.created_at}
                country_code={listing.country_code}
                user_id={listing.user_id}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // Desktop: Grid view with content-visibility optimization
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 listings-grid">
      {listings.map((listing, index) => (
        <div key={listing.id} data-flip-id={listing.id} className="listing-card-container">
          <ListingCardV2
            listing={listing}
            onCompareToggle={onCompareToggle}
            isInComparison={comparedIds.includes(listing.id)}
            priority={index < priorityCount}
          />
        </div>
      ))}
    </div>
  );
};
